import { useCallback, useEffect } from "react"
import { FormProvider, useFieldArray, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FormField } from "@/components/ui/form"
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from "@/components/ui/radio-group"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { stringRequired } from "@/models/common"
import { useHealthInformationCopilotContext } from "@/modules/patients/contexts/health-information-copilot-context"
import { CommentFormItem } from "@/modules/patients/detail/health-information/comment-form-item"
import { PreventionProgramsContent } from "@/modules/patients/detail/health-information/condition/prevention-programs"
import { ConfirmedBy } from "@/modules/patients/detail/health-information/confirmed-by"
import { IcdCodeSearch } from "@/modules/patients/detail/health-information/icd-code-search"
import { useConfirmDialogStoreContext } from "@/modules/patients/detail/store/useConfirmDialogStore"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import {
  HealthInformationStatus,
  PatientConditionInformationOutputFragment$key,
} from "@/modules/patients/services/__generated__/PatientConditionInformationOutputFragment.graphql"
import { Ch_updateAllPatientConditionsInput } from "@/modules/patients/services/ch/__generated__/chUpdateAllPatientConditionsMutation.graphql"
import { Ch_verifyHealthInformationInput } from "@/modules/patients/services/ch/__generated__/chVerifyHealthInformationMutation.graphql"
import { De_updateAllPatientConditionsInput } from "@/modules/patients/services/de/__generated__/deUpdateAllPatientConditionsMutation.graphql"
import { De_verifyHealthInformationInput } from "@/modules/patients/services/de/__generated__/deVerifyHealthInformationMutation.graphql"
import { PatientConditionInformationOutputFragment } from "@/modules/patients/services/PatientConditionInformationOutputFragment"
import { usePatientCondition } from "@/modules/patients/services/usePatientCondition"
import { usePatientPrevention } from "@/modules/patients/services/usePatientPrevention"
import { useUpdateAllPatientConditionsMutation } from "@/modules/patients/services/useUpdateAllPatientConditionsMutation"
import { useVerifyHealthInformation } from "@/modules/patients/services/useVerifyHealthInformation"
import { useDrawerStore } from "@/stores/useDrawerStore"

const conditionSchema = z.object({
  icdCode: stringRequired,
  description: z.string().nullable(),
  comment: z.string().nullable(),
})

const patientConditionSchema = z.object({
  conditions: z.array(conditionSchema),
  conditionStatus: z.custom<HealthInformationStatus>().optional(),
})

type PatientConditionForm = z.infer<typeof patientConditionSchema>

const ConditionsDrawer = ({
  canVerify,
  verifiedBy,
  verifiedAt,
  appointmentId,
  isEditMode,
}: {
  canVerify: boolean
  verifiedBy: string
  verifiedAt: string
  appointmentId: string | null
  isEditMode: boolean
}) => {
  const patientId = usePatientParam()
  const { t } = useTranslation()
  const closeDrawer = useDrawerStore((state) => state.closeDrawer)
  const setDeleteConfirmationDialogParams = useConfirmDialogStoreContext(
    (state) => state.setDeleteConfirmationDialogParams,
  )
  const closeDeleteConfirmationDialog = useConfirmDialogStoreContext((state) => state.closeDeleteConfirmationDialog)
  const country = useCountryParam()
  const handleUpdateAllCondition = useUpdateAllPatientConditionsMutation(country)
  const handleVerifyHealthInformation = useVerifyHealthInformation(country)

  const dataRef = usePatientCondition(patientId, country)
  const data = useFragment<PatientConditionInformationOutputFragment$key>(
    PatientConditionInformationOutputFragment,
    dataRef,
  )
  const { conditions, status: conditionStatus } = data

  const { preventionProgramItems } = usePatientPrevention(patientId, country)

  const selectedCondition = useHealthInformationCopilotContext((v) => v.conditions.selected)
  const resetSelectedCondition = useHealthInformationCopilotContext((v) => v.conditions.resetSelected)
  const setSelectedCondition = useHealthInformationCopilotContext((state) => state.conditions.setSelected)

  const initialConditions = conditions.map((condition) => ({
    icdCode: condition.icdDiagnosis.diagnosisCode,
    description: condition.icdDiagnosis.text,
    comment: condition.comment,
  }))

  const form = useForm<PatientConditionForm>({
    resolver: zodResolver(patientConditionSchema),
    defaultValues: {
      conditions: initialConditions,
      conditionStatus,
    },
  })
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { isDirty },
  } = form

  const { fields, insert, remove } = useFieldArray({ control, name: "conditions" })

  const handleSelectIcdCode = useCallback(
    (diagnosisCode: string, text: string) => {
      const sortedCodes = [...fields.map((field) => field.icdCode), diagnosisCode].sort()
      const addedIndex = sortedCodes.indexOf(diagnosisCode)
      insert(
        addedIndex,
        { icdCode: diagnosisCode, description: text, comment: null },
        {
          focusName: `conditions.${addedIndex}.comment`,
        },
      )
    },
    [fields, insert],
  )

  const handleSaveConditions = (data: PatientConditionForm) => {
    if (isDirty) {
      const submitData: De_updateAllPatientConditionsInput & Ch_updateAllPatientConditionsInput = {
        patientId,
        conditions: data.conditions.map((condition) => ({
          diagnosisCode: condition.icdCode,
          comment: condition.comment,
        })),
        conditionStatus: data.conditionStatus,
      }

      handleUpdateAllCondition(submitData, closeDrawer)
    } else closeDrawer()
  }
  const handleVerifyConditions = () => {
    const verification: De_verifyHealthInformationInput & Ch_verifyHealthInformationInput = {
      patientId,
      healthInformationType: "CONDITION",
      appointmentId: appointmentId!,
    }

    handleVerifyHealthInformation(verification, closeDrawer)
  }
  const onConditionDelete = (index: number, description: string | null) => {
    setDeleteConfirmationDialogParams({
      title: t("patient.healthInformation.deleteHealthInformation.title", {
        item: description ?? "",
      }),
      content: t("episodeOfCare.deleteProgressNote.content"),
      handleDelete: () => {
        const conditions = getValues("conditions")
        if (conditions.length === 1) {
          setValue("conditionStatus", "UNKNOWN")
        }
        remove(index)
        closeDeleteConfirmationDialog()
      },
    })
  }

  const RADIO_ITEMS: { value: HealthInformationStatus; label: string }[] = [
    {
      value: "UNKNOWN",
      label: t("patient.healthInformation.emptyQuestions.Unknown"),
    },
    {
      value: "NONE",
      label: t("common.no"),
    },
  ]

  const handleClose = useCallback(() => {
    FaroInsightsWrapper.pushEvent("healthInformation_conditions_close")
    closeDrawer()
    resetSelectedCondition()
  }, [closeDrawer, resetSelectedCondition])

  useDirtyDrawerMaker(isDirty, handleClose)

  useEffect(() => {
    if (selectedCondition) {
      const conditions = getValues("conditions")
      const isExistingConditions = conditions.find((field) => field.icdCode === selectedCondition.diagnosisCode)
      if (!isExistingConditions) {
        handleSelectIcdCode(selectedCondition.diagnosisCode, selectedCondition.text)
      }
      setSelectedCondition(null)
    }
  }, [selectedCondition, getValues, handleSelectIcdCode, setSelectedCondition])

  return (
    <Drawer open onOpenChange={handleClose}>
      <DrawerContent data-testid="health-condition-drawer-content">
        <DrawerClose className="top-2 right-2" />
        <DrawerHeader className="border-b py-2">
          <DrawerTitle
            className="animate-fade-in-bottom text-on-surface-subdued text-sm leading-10 font-medium"
            data-testid="conditions-drawer-title">
            {t("patient.healthInformation.conditions.title")}
          </DrawerTitle>
          {isEditMode && (
            <IcdCodeSearch
              searchType="CONDITION"
              searchName="condition"
              onIcdCodeSelected={handleSelectIcdCode}
              searchInputPlaceHolder={t("patient.healthInformation.conditions.addCondition")}
              addedIcdCodes={fields.map((field) => field.icdCode)}
            />
          )}
        </DrawerHeader>
        <DrawerBody className="p-0" data-testid="condition-drawer-body-section">
          <ScrollWrapper className="p-4">
            <div className="flex flex-col gap-4">
              {isEditMode && (
                <PreventionProgramsContent
                  isOpenConditionDrawer={true}
                  patientPreventionItems={preventionProgramItems}
                  closeDrawer={closeDrawer}
                />
              )}
              <FormProvider {...form}>
                {fields.length === 0 && isEditMode && (
                  <div className="flex flex-col gap-2">
                    <span className="text-on-surface-subdued text-sm leading-5" data-testid="condition-status-question">
                      {t("patient.healthInformation.conditions.emptyQuestions")}
                    </span>
                    <FormField
                      name="conditionStatus"
                      control={control}
                      render={({ field: { value: val, onChange } }) => (
                        <RadioGroup
                          data-testid="condition-unknown-select"
                          defaultValue={conditionStatus}
                          className="auto-cols-min grid-flow-col"
                          value={val}
                          onValueChange={onChange}>
                          {RADIO_ITEMS.map(({ value, label }) => (
                            <RadioGroupItem
                              key={label}
                              data-testid={`condition-radio-${value}`}
                              value={value.toString()}
                              id={`option-${value}`}>
                              <RadioGroupLabel htmlFor={`option-${value}`}>{label}</RadioGroupLabel>
                            </RadioGroupItem>
                          ))}
                        </RadioGroup>
                      )}
                    />
                  </div>
                )}
                {fields.length !== 0 &&
                  fields.map((field, index) => (
                    <CommentFormItem
                      key={field.id}
                      dataTestId="condition"
                      isEditMode={isEditMode}
                      title={`${field.icdCode} - ${field.description}`}
                      onDelete={() => onConditionDelete(index, field.description)}
                      commentFieldName={`conditions.${index}.comment`}
                    />
                  ))}
              </FormProvider>
            </div>
          </ScrollWrapper>
        </DrawerBody>
        <DrawerFooter className="justify-end gap-2 overflow-hidden" data-testid="condition-drawer-footer-section">
          {verifiedBy !== "" && <ConfirmedBy verifiedBy={verifiedBy} verifiedAt={verifiedAt} />}
          <Button variant="secondary" onClick={handleClose} data-testid="condition-close-btn">
            {isEditMode ? t("common.button.cancel") : t("common.button.close")}
          </Button>
          {isEditMode && !canVerify && (
            <Button
              onClick={() => {
                FaroInsightsWrapper.pushEvent("healthInformation_conditions_save")
                handleSubmit(handleSaveConditions)()
              }}
              disabled={!isDirty}
              data-testid="condition-save-btn">
              {t("common.button.save")}
            </Button>
          )}
          {isEditMode && canVerify && (
            <Button
              onClick={() => {
                handleSubmit(handleSaveConditions)()
                handleVerifyConditions()
              }}
              data-testid="condition-verify-btn">
              {t("common.button.verify")}
            </Button>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

export { ConditionsDrawer, type PatientConditionForm }
